import React from 'react';
import { Building2, Store, PenTool, Sparkles, Target, Rocket } from 'lucide-react';
import CTAButton from './CTAButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import SectionLayout from './SectionLayout';
import { Link } from 'react-router-dom';

interface UseCaseProps {
  text: string;
}

const UseCase: React.FC<UseCaseProps> = ({
  text
}) => <div className="flex items-start gap-2 mb-2 animate-fade-in">
    <div className="min-w-5 pt-0.5">
      <div className="w-4 h-4 rounded-full bg-[#FEE6D8] flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-[#003a70]"></div>
      </div>
    </div>
    <p className="text-sm text-gray-600">{text}</p>
  </div>;

interface CategoryCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  useCases: string[];
  iconClassName?: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  icon,
  title,
  description,
  useCases,
  iconClassName
}) => <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 h-full
                 hover:shadow-xl hover:border-[#FEE6D8] transition-all duration-500
                 hover:-translate-y-1 flex flex-col">
    <div className="flex items-center gap-4 mb-5">
      <div className={cn("p-3.5 bg-[#FEE6D8] text-[#003a70] rounded-xl transition-all duration-300", "hover:scale-105 group", iconClassName)}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-[#003a70]">{title}</h3>
    </div>
    
    <p className="text-gray-600 mb-5">{description}</p>
    
    <div className="mt-auto">
      <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
        <Sparkles size={14} className="text-[#003a70]" />
        <span>Use Cases:</span>
      </h4>
      <div className="space-y-2.5">
        {useCases.map((useCase, index) => <UseCase key={index} text={useCase} />)}
      </div>
    </div>
  </div>;

const categoryData = [
  {
    icon: <Rocket size={28} className="transform transition-transform group-hover:rotate-12" />,
    title: "Digital Agencies",
    description: "Deliver more value to clients with AI-driven automation and engagement tools.",
    useCases: ["Automate lead qualification with AI-powered chatbots.", "Offer AI-driven customer support as a value-added service.", "Generate AI-powered marketing copy and interactive experiences."],
    iconClassName: "bg-[#FEE6D8]"
  },
  {
    icon: <Target size={28} className="transform transition-transform group-hover:scale-110" />,
    title: "Small Businesses",
    description: "Boost productivity and customer engagement with AI-powered forms, assistants, and workflows.",
    useCases: ["Automate appointment booking and customer inquiries.", "Capture leads and personalize responses instantly.", "Use AI-powered knowledge bases for 24/7 customer support."],
    iconClassName: "bg-[#FEE6D8]"
  },
  {
    icon: <PenTool size={28} className="transform transition-transform group-hover:-rotate-12" />,
    title: "Content Creators",
    description: "Enhance audience engagement and content distribution with AI-driven interactions.",
    useCases: ["Use AI chatbots to recommend personalized content.", "Create AI-powered interactive quizzes and forms.", "Automate community engagement on websites and social platforms."],
    iconClassName: "bg-[#FEE6D8]"
  }
];

const WhoIsAskSpotFor = () => {
  const isMobile = useIsMobile();
  
  return <SectionLayout backgroundPattern="dots" enableMagicBackground={true} paddingY="py-16 md:py-24" className="md:-mt-24">
      <div className="relative z-10">
        <div className="text-center mb-14 relative animate-fade-in">
          <div className="inline-block mb-3">
            
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#003a70] mb-4 relative">
            Who is <span className="relative inline-block">
              AskSpot 
              <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-[#FEE6D8] rounded-full"></span>
            </span> For?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            AskSpot is designed for businesses, agencies, and creators who want to automate, 
            engage, and scale—without the complexity.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 px-4 md:px-0 mb-12">
          {categoryData.map((category, index) => <div key={index} className="animate-fade-in" style={{
          animationDelay: `${index * 200}ms`
        }}>
              <CategoryCard {...category} />
            </div>)}
        </div>
        
        <div className="relative z-10">
          <div className="bg-[#003a70] rounded-2xl p-6 md:p-8 lg:p-12 text-center shadow-xl 
                         relative overflow-hidden animate-fade-in" style={{
            animationDelay: '600ms'
          }}>
            <div className="absolute top-0 right-0 w-24 md:w-40 h-24 md:h-40 bg-[#FEE6D8]/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-20 md:w-32 h-20 md:h-32 bg-[#FEE6D8]/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <p className="text-base md:text-xl font-medium text-white mb-3 md:mb-5 relative z-10 max-w-md mx-auto leading-tight">
              Find Out How AskSpot Can Work for You
            </p>
            <div className="flex justify-center relative z-10">
              <Link to="/pricing">
                <CTAButton variant="primary" className={cn(
                  "px-6 md:px-8 py-3 md:py-3.5 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold", 
                  "bg-[#FEE6D8] text-[#003a70] hover:-translate-y-0.5", 
                  isMobile ? 'text-sm w-full max-w-xs' : 'text-base'
                )}>
                  Start Your 30-Day Free Trial →
                </CTAButton>
              </Link>
            </div>
          </div>
        </div>
        
      </div>
    </SectionLayout>;
};

export default WhoIsAskSpotFor;
